import { Empty } from 'cpool-components/state/Empty';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LuMessageCircle } from 'react-icons/lu';

import { useApplicantStartChat } from '~/features/whatsapp/hooks/useApplicantStartChat';
import {
    START_CHAT_MISSING_REASONS,
    type StartChatMissingReason,
} from '~/features/whatsapp/utils/resolveApplicantStartChat';

type Props = {
    loanRequestId: string;
    applicantSlot?: 1 | 2;
};

const MISSING_REASON_KEYS: Record<StartChatMissingReason, ParseKeys> = {
    [START_CHAT_MISSING_REASONS.COAPPLICANT_HIDDEN]: 'WHATSAPP_CHAT.LIST.LEAD_SCOPED_COAPPLICANT_HIDDEN',
    [START_CHAT_MISSING_REASONS.DATA_PROTECTION]: 'LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING',
    [START_CHAT_MISSING_REASONS.NOT_PERMITTED]: 'WHATSAPP_CHAT.LIST.LEAD_SCOPED_NOT_PERMITTED',
    [START_CHAT_MISSING_REASONS.NO_APPLICANT_PHONE]: 'WHATSAPP_CHAT.LIST.LEAD_SCOPED_NO_APPLICANT_PHONE',
    [START_CHAT_MISSING_REASONS.NO_PHONE_NUMBER]: 'WHATSAPP_CHAT.LIST.LEAD_SCOPED_NO_PHONE_NUMBER',
};

/**
 * Reason-only empty state for a lead's chat list/pane when no chat exists yet. Never starts a
 * chat itself — auto-start lives in the lead "Chat" button's open-path and in `ChatView`'s KN2
 * effect. `missingReason` covers every blocked reason now, so a payload with no missingReason
 * only means eligibility changed after the open-path already landed here — tell the user to
 * reopen rather than show a blank title.
 */
export const ChatBlockedReason = ({ applicantSlot = 1, loanRequestId }: Props) => {
    const { t } = useTranslation();
    const { kn1, kn2 } = useApplicantStartChat(loanRequestId);
    const slot = applicantSlot === 2 ? kn2 : kn1;

    let description: string | undefined;
    if (slot.missingReason) {
        description = t(MISSING_REASON_KEYS[slot.missingReason]);
    } else if (slot.payload) {
        description = t('WHATSAPP_CHAT.LIST.LEAD_SCOPED_REOPEN_TO_START');
    }

    return (
        <Empty icon={<LuMessageCircle />} title={t('WHATSAPP_CHAT.LIST.LEAD_SCOPED_EMPTY')} description={description} />
    );
};
